/* eslint-disable react/require-default-props */
import React from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  useOverflowMenu,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
  useIsOverflowItemVisible,
  MenuItem,
  MenuList,
  Label,
} from '@fluentui/react-components';

import {
  bundleIcon,
  MoreHorizontalFilled,
  MoreHorizontalRegular,
} from '@fluentui/react-icons';
import type { ARIAButtonElement } from '@fluentui/react-aria';
import { usePopOverContainerStyles } from './styles';
import { SideNavigationItemProps, SideNavigationAccordionItemProps } from './types';

const MoreHorizontal = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular);

const useOverflowMenuStyles = makeStyles({
  menu: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  menuButton: {
    alignSelf: 'center',
  },
  container: {
    textAlign: 'center',
  },
});

type OverflowMenuProps = {
  // eslint-disable-next-line no-unused-vars
  onTabSelect: (tabId: string) => void,
  items: Array<SideNavigationItemProps>
};

type Tab = {
  id: string;
  key?: string;
  text: string;
  icon?: React.FC<{}>;
};

type OverflowMenuItemProps = {
  tab: Tab;
  onClick: React.MouseEventHandler<ARIAButtonElement<'div'>>;
  className?: string
};

const OverflowMenuItem = (props: OverflowMenuItemProps) => {
  const { tab, onClick, className } = props;
  const isVisible = useIsOverflowItemVisible(tab.key);

  if (isVisible) {
    return null;
  }

  return (
    <MenuItem key={tab.key} onClick={onClick} className={className} id={tab.id}>
      {tab.text}
    </MenuItem>
  );
};

const renderPopoverMenu = (
  item: SideNavigationItemProps,
  // eslint-disable-next-line no-unused-vars
  onItemClick: (i: SideNavigationItemProps) => void,
  className: string,
) => {
  const { isAccordion, secondaryNavItems } = item;
  const hasAccordion = isAccordion && secondaryNavItems;
  if (hasAccordion) {
    return (
      <div key={item.key}>
        <Label>{item.text}</Label>
        {
          secondaryNavItems?.map((secondaryitem) => (
            <OverflowMenuItem
              key={secondaryitem.key}
              tab={secondaryitem as SideNavigationAccordionItemProps}
              className={mergeClasses('side-navigation-more-item', className)}
              onClick={() => onItemClick(secondaryitem as SideNavigationAccordionItemProps)}
            />
          ))
        }
      </div>
    );
  }

  return (
    <OverflowMenuItem
      key={item.key}
      tab={item}
      className={mergeClasses('side-navigation-more-item', className)}
      onClick={() => onItemClick(item)}
    />
  );
};

export const SideNavigationPopoverMenu = (props: OverflowMenuProps) => {
  const { onTabSelect, items } = props;
  const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();
  const [open, setOpen] = React.useState(false);

  const styles = useOverflowMenuStyles();
  const popoverStyle = usePopOverContainerStyles();

  const onItemClick = (item: SideNavigationItemProps) => {
    onTabSelect(item.key);
    item.onClick?.();
    setOpen(false);
  };

  if (!isOverflowing) {
    return null;
  }

  return (
    <Popover
      open={open}
      withArrow
      trapFocus
      positioning="after"
      onOpenChange={(e, data) => {
        setOpen(data.open || false);
      }}
    >
      <PopoverTrigger disableButtonEnhancement>
        <div className={styles.container}>
          <Button
            appearance="transparent"
            className={mergeClasses('side-navigation-more-button', styles.menuButton)}
            ref={ref}
            icon={<MoreHorizontal />}
            aria-label={`${overflowCount} more tabs`}
            role="tab"
          />
        </div>
      </PopoverTrigger>
      <PopoverSurface className={mergeClasses('side-navigation-popover', popoverStyle.popOverContainer)}>
        <MenuList className={mergeClasses('side-navigation-more-items', styles.menu)}>
          {
            items.map((item) => (renderPopoverMenu(item, onItemClick, styles.menu)))
          }
        </MenuList>
      </PopoverSurface>
    </Popover>
  );
};
